/**
 * Graph identifier helpers for multi-target operations.
 *
 * The backend emits identifiers in two shapes:
 *   - Legacy (single-target): ``COA Overview``, ``STIX Overview``, ``Operation Overview``.
 *   - Multi-target: ``coa:<target_id>``, ``stix:<target_id>``, ``operation`` (combined).
 *
 * Consumers parse identifiers via ``parseGraphId`` into a ``{ target_id, view }``
 * shape and rebuild them via ``buildGraphId`` when routing UI actions back into
 * Recoil or socket emissions. The atom storing the selection stays ``string``
 * for backward compatibility with other Chainlit forks.
 */

export type GraphView = 'COA' | 'STIX' | 'Operation';

export interface ParsedGraphId {
  target_id: string | null;
  view: GraphView;
  raw: string;
}

const LEGACY_BY_NAME: Record<string, GraphView> = {
  'COA Overview': 'COA',
  'STIX Overview': 'STIX',
  'Operation Overview': 'Operation',
  'COA All Targets': 'COA',
  'STIX All Targets': 'STIX'
};

const PREFIX_TO_VIEW: Record<string, GraphView> = {
  coa: 'COA',
  stix: 'STIX'
};

const VIEW_TO_PREFIX: Record<GraphView, string> = {
  COA: 'coa',
  STIX: 'stix',
  Operation: 'operation'
};

export function parseGraphId(raw: string | null | undefined): ParsedGraphId | null {
  if (!raw || raw === 'none') return null;
  const normalized = String(raw);

  if (LEGACY_BY_NAME[normalized]) {
    return {
      target_id: null,
      view: LEGACY_BY_NAME[normalized],
      raw: normalized
    };
  }

  if (normalized === 'operation') {
    return { target_id: null, view: 'Operation', raw: normalized };
  }

  const colonIdx = normalized.indexOf(':');
  if (colonIdx > 0) {
    const prefix = normalized.slice(0, colonIdx).toLowerCase();
    const tail = normalized.slice(colonIdx + 1);
    const view = PREFIX_TO_VIEW[prefix];
    if (view && tail) {
      return { target_id: tail, view, raw: normalized };
    }
  }

  return { target_id: null, view: 'COA', raw: normalized };
}

export function buildGraphId(target_id: string | null, view: GraphView): string {
  if (view === 'Operation') {
    return 'Operation Overview';
  }
  if (!target_id) {
    return view === 'COA' ? 'COA Overview' : 'STIX Overview';
  }
  return `${VIEW_TO_PREFIX[view]}:${target_id}`;
}

export function isTaskGraphView(view: GraphView): boolean {
  return view === 'COA' || view === 'Operation';
}

export function groupGraphsByTarget(graphIds: readonly string[]): {
  operationIds: string[];
  targets: Record<string, { coa?: string; stix?: string }>;
  ungrouped: string[];
} {
  const operationIds: string[] = [];
  const targets: Record<string, { coa?: string; stix?: string }> = {};
  const ungrouped: string[] = [];

  for (const raw of graphIds) {
    const parsed = parseGraphId(raw);
    if (!parsed) {
      ungrouped.push(raw);
      continue;
    }
    if (parsed.view === 'Operation') {
      operationIds.push(raw);
      continue;
    }
    if (parsed.target_id) {
      const bucket = targets[parsed.target_id] ?? {};
      if (parsed.view === 'COA') bucket.coa = raw;
      else if (parsed.view === 'STIX') bucket.stix = raw;
      targets[parsed.target_id] = bucket;
    } else {
      ungrouped.push(raw);
    }
  }

  return { operationIds, targets, ungrouped };
}
